// © Thibault Sabathier, 2026. frontend/src/utils/imageCompression.js
//
// Réduction des images AVANT envoi, pour que le corps multipart tienne sous la
// limite nginx (`client_max_body_size 20M`). Sans ça, sélectionner 5 photos de
// téléphone produit un corps de plusieurs Mo rejeté en 413 par nginx — donc
// AVANT d'atteindre Express, donc sans message exploitable côté client.
//
// ── POURQUOI 1200 px / q0.8 ────────────────────────────────────────────────
// Ce sont les valeurs déjà retenues ailleurs dans le dépôt : `optimizeImage()`
// côté backend (sharp, maxWidth 1200) et l'ancien compresseur de
// `components/Posts/UploadComponent.js`. Introduire une troisième valeur
// n'apporterait rien : le backend re-encoderait à 1200 de toute façon, et les
// octets gagnés en amont seraient jetés en aval.
//
// ── LA COUTURE `compressOne` ───────────────────────────────────────────────
// Tout ce qui touche au DOM (Image, canvas, ObjectURL) est isolé dans
// `browserCompressOne`, injectable via l'option `compressOne`. Raison : le
// runner de tests du dépôt tourne en `environment: 'node'` SANS jsdom
// (cf. `vitest.config.mjs`, choix assumé). Sans cette couture, la logique de
// classification et de budget ne serait testable qu'au prix d'un jsdom.
//
// ── CE QUI N'EST VOLONTAIREMENT PAS FAIT ───────────────────────────────────
// Pas de conversion HEIC/HEIF. Elle imposerait une dépendance lourde
// (heic2any ≈ 1,4 Mo de bundle) pour un cas JAMAIS observé en production : sur
// 14 jours de logs nginx et 266 requêtes d'upload abouties, un seul 400, aucun
// rejet de type MIME. Le HEIC est donc détecté et refusé avec un message
// explicite ; la conversion viendra si les logs la justifient un jour.

/** Taille max par fichier — alignée sur multer (`uploadPosts`, 10 Mo). */
export const MAX_FILE_SIZE = 10 * 1024 * 1024

/**
 * Budget cumulé du corps de requête.
 * nginx coupe à 20 Mo ; on garde ~2 Mo de marge pour l'overhead multipart
 * (frontières, en-têtes de parties) et les champs texte du formulaire
 * (contenu, ciblage, membres identifiés…).
 */
export const MAX_TOTAL_SIZE = 18 * 1024 * 1024

/** Types que sharp/canvas savent ré-encoder sans rien perdre de visible. */
const COMPRESSIBLE_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

/**
 * Le GIF traverse SANS ré-encodage : le passer dans un canvas l'aplatirait en
 * une image fixe, l'animation disparaîtrait sans erreur ni message. multer
 * l'accepte tel quel, on le laisse tel quel.
 */
const PASSTHROUGH_MIME = ['image/gif']

/** Liste multer (`imageFileFilter`). Hors de cette liste, le backend répond 400. */
const ACCEPTED_MIME = [...COMPRESSIBLE_MIME, ...PASSTHROUGH_MIME]

/** Formats Apple : indécodables par canvas hors Safari, et refusés par multer. */
const HEIC_MIME = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']
const HEIC_EXTENSION = /\.(heic|heif)$/i

export const ERROR_FILE_TOO_LARGE = 'FILE_TOO_LARGE'
export const ERROR_UNSUPPORTED_FORMAT = 'UNSUPPORTED_FORMAT'
export const ERROR_HEIC_FORMAT = 'HEIC_FORMAT'

/**
 * Classe un fichier sans le lire — logique pure, testable sans DOM.
 *
 * Le HEIC est distingué des autres formats refusés parce qu'il appelle un
 * conseil actionnable (« exportez en JPEG ») là où un .txt renommé n'en appelle
 * aucun.
 *
 * @param {File} file
 * @returns {'compress'|'passthrough'|'oversize'|'heic'|'unsupported'}
 */
export const classifyFile = (file) => {
  const mime = (file?.type || '').toLowerCase()
  const name = file?.name || ''

  // Le HEIC est testé AVANT la taille : sur un HEIC de 12 Mo, « exportez-la en
  // JPEG » est un conseil utile, « elle dépasse 10 Mo » ne l'est pas.
  // Certains navigateurs renvoient un `type` vide sur un HEIC — d'où l'extension.
  if (HEIC_MIME.includes(mime) || HEIC_EXTENSION.test(name)) return 'heic'

  if (typeof file?.size === 'number' && file.size > MAX_FILE_SIZE) return 'oversize'

  if (PASSTHROUGH_MIME.includes(mime)) return 'passthrough'
  if (COMPRESSIBLE_MIME.includes(mime)) return 'compress'

  return 'unsupported'
}

/** Un MIME que multer acceptera : condition du repli sur l'original. */
export const isAcceptedByBackend = (file) =>
  ACCEPTED_MIME.includes((file?.type || '').toLowerCase())

/**
 * Pèse une sélection ENTIÈRE.
 *
 * Fonction à part, et non un calcul local à `compressImages`, parce que le
 * budget porte sur la requête — donc sur toutes les photos retenues, pas sur
 * le dernier lot compressé. Une sélection se fait souvent en plusieurs fois
 * (« ajouter des photos » puis « en ajouter encore ») : chaque lot peut tenir
 * dans le budget alors que leur somme le dépasse, et c'est exactement ce
 * chemin qui ramènerait le 413.
 *
 * @param {File[]} files Toutes les photos retenues pour l'envoi.
 * @param {number} [maxTotalSize]
 * @returns {{totalBytes: number, overBudget: boolean}}
 */
export const computeBudget = (files, maxTotalSize = MAX_TOTAL_SIZE) => {
  const totalBytes = (Array.isArray(files) ? files : []).reduce(
    (sum, file) => sum + (file?.size || 0),
    0,
  )
  return { totalBytes, overBudget: totalBytes > maxTotalSize }
}

/**
 * Partie DOM, remplaçable en test. Ré-encode en JPEG à `maxDimension` px sur le
 * plus grand côté.
 *
 * Rejette explicitement sur décodage impossible : c'est ce qui manquait à
 * l'ancien compresseur de `UploadComponent.js`, dont la Promise ne se résolvait
 * JAMAIS sur un fichier indécodable — bouton bloqué sur « Envoi… », sans erreur.
 * Chaque sortie passe par `URL.revokeObjectURL`.
 *
 * @param {File} file
 * @param {{maxDimension: number, quality: number}} options
 * @returns {Promise<File>}
 */
export const browserCompressOne = (file, { maxDimension, quality }) =>
  new Promise((resolve, reject) => {
    // ObjectURL plutôt que FileReader + data URL : une data URL est du base64,
    // soit ~33 % de mémoire en plus par image, sur 10 images d'un coup.
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()

    const cleanup = () => URL.revokeObjectURL(objectUrl)

    img.onload = () => {
      try {
        let { width, height } = img
        if (!width || !height) {
          cleanup()
          reject(new Error('Dimensions nulles'))
          return
        }

        const ratio = Math.min(1, maxDimension / Math.max(width, height))
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        canvas.getContext('2d').drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            cleanup()
            // toBlob peut rendre null (mémoire, format refusé) : sans ce garde,
            // `new File([null])` lèverait plus loin, hors du catch utile.
            if (!blob) {
              reject(new Error('toBlob a rendu null'))
              return
            }
            resolve(
              new File([blob], toJpegName(file.name), {
                type: 'image/jpeg',
                lastModified: file.lastModified,
              }),
            )
          },
          'image/jpeg',
          quality,
        )
      } catch (err) {
        cleanup()
        reject(err)
      }
    }

    img.onerror = () => {
      cleanup()
      reject(new Error('Décodage impossible'))
    }

    img.src = objectUrl
  })

/**
 * La sortie est du JPEG : le nom doit le dire. multer filtre sur le MIME mais
 * conserve l'extension d'origine pour nommer le fichier stocké — un `.png`
 * contenant du JPEG produirait un fichier dont l'extension ment.
 */
export const toJpegName = (name) => {
  if (!name) return 'image.jpg'
  const dot = name.lastIndexOf('.')
  return `${dot > 0 ? name.slice(0, dot) : name}.jpg`
}

/**
 * Prépare une sélection pour l'envoi.
 *
 * Garanties :
 *  - une image en échec n'annule JAMAIS les autres (traitement fichier par
 *    fichier, erreurs collectées) ;
 *  - aucune Promise pendante : toute branche résout ou rejette ;
 *  - repli sur l'original UNIQUEMENT si son MIME est accepté par multer, et
 *    seulement APRÈS échec de la compression — jamais pour un HEIC, que le
 *    backend refuserait de toute façon ;
 *  - si la compression fait GONFLER le fichier (petits PNG déjà optimisés),
 *    on garde l'original : le but est de réduire le corps, pas de ré-encoder.
 *
 * Séquentiel et non parallèle : dix `drawImage` concurrents saturent la mémoire
 * d'un mobile. Le coût est du temps, pas des échecs — d'où `onProgress`.
 *
 * @param {File[]} files
 * @param {object} [options]
 * @param {Function} [options.compressOne] Couture DOM (injectée en test).
 * @param {number}   [options.maxDimension]
 * @param {number}   [options.quality]
 * @param {number}   [options.maxTotalSize]
 * @param {(done: number, total: number) => void} [options.onProgress]
 * @returns {Promise<{files: File[], errors: Array<{name: string, code: string}>,
 *                    totalBytes: number, overBudget: boolean, fallbackCount: number}>}
 */
export const compressImages = async (files, options = {}) => {
  const {
    compressOne = browserCompressOne,
    maxDimension = 1200,
    quality = 0.8,
    maxTotalSize = MAX_TOTAL_SIZE,
    onProgress,
  } = options

  const list = Array.isArray(files) ? files : []
  const kept = []
  const errors = []
  let fallbackCount = 0

  for (let i = 0; i < list.length; i += 1) {
    const file = list[i]

    switch (classifyFile(file)) {
      case 'heic':
        errors.push({ name: file?.name || '', code: ERROR_HEIC_FORMAT })
        break

      case 'oversize':
        errors.push({ name: file?.name || '', code: ERROR_FILE_TOO_LARGE })
        break

      case 'unsupported':
        errors.push({ name: file?.name || '', code: ERROR_UNSUPPORTED_FORMAT })
        break

      case 'passthrough':
        kept.push(file)
        break

      case 'compress':
      default:
        try {
          const compressed = await compressOne(file, { maxDimension, quality })
          if (compressed && compressed.size < file.size) {
            kept.push(compressed)
          } else {
            kept.push(file)
          }
        } catch {
          // Échec isolé : l'original part tel quel puisque multer l'accepte
          // (la classification `compress` l'a déjà garanti). Le garde de budget
          // ci-dessous reste le filet si l'ensemble ne tient pas.
          kept.push(file)
          fallbackCount += 1
        }
        break
    }

    if (onProgress) onProgress(i + 1, list.length)
  }

  // ⚠️ Ce budget ne porte que sur CE lot. L'appelant qui accumule plusieurs
  // sélections doit repeser l'ensemble avec `computeBudget()` — deux lots de
  // 10 MiB passent ici séparément et dépassent une fois réunis.
  const { totalBytes, overBudget } = computeBudget(kept, maxTotalSize)

  return {
    files: kept,
    errors,
    totalBytes,
    overBudget,
    fallbackCount,
  }
}
